// Deals constants — single source of truth. Stage keys MUST match the backend
// `DEAL_STAGES` (server.js) exactly.

import type { BadgeTone } from '@/shared/components/ui/Badge';
import type { DealStage } from './types';

export interface StageMeta {
  key: DealStage;
  label: string;
  tone: BadgeTone;
}

export const DEAL_STAGES: StageMeta[] = [
  { key: 'prospecting', label: 'Prospecting', tone: 'neutral' },
  { key: 'qualification', label: 'Qualification', tone: 'info' },
  { key: 'proposal', label: 'Proposal', tone: 'primary' },
  { key: 'negotiation', label: 'Negotiation', tone: 'warning' },
  { key: 'closed_won', label: 'Closed Won', tone: 'success' },
  { key: 'closed_lost', label: 'Closed Lost', tone: 'danger' },
];

export const STAGE_LABELS: Record<string, string> = Object.fromEntries(
  DEAL_STAGES.map((s) => [s.key, s.label]),
);

export const STAGE_TONES: Record<string, BadgeTone> = Object.fromEntries(
  DEAL_STAGES.map((s) => [s.key, s.tone]),
);

export const STAGE_OPTIONS = DEAL_STAGES.map((s) => ({ value: s.key, label: s.label }));

export const CLOSED_STAGES: DealStage[] = ['closed_won', 'closed_lost'];

export const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

export const PRIORITY_TONES: Record<string, BadgeTone> = {
  low: 'neutral',
  medium: 'info',
  high: 'danger',
};

/** Selectable currencies. PKR is intentionally NOT offered. */
export const CURRENCY_OPTIONS = [
  { value: 'INR', label: 'INR (₹)' },
  { value: 'USD', label: 'USD ($)' },
  { value: 'EUR', label: 'EUR (€)' },
  { value: 'AED', label: 'AED (د.إ)' },
  { value: 'GBP', label: 'GBP (£)' },
];

const CURRENCY_SYMBOLS: Record<string, string> = {
  INR: '₹',
  USD: '$',
  EUR: '€',
  AED: 'AED ',
  GBP: '£',
};

/**
 * Format a monetary amount with its OWN currency symbol. Currencies are never
 * aggregated across types — each value is rendered with its own currency.
 */
export function formatMoney(value: number | undefined | null, currency = 'INR'): string {
  const amount = Number(value ?? 0);
  const symbol = CURRENCY_SYMBOLS[currency];
  const formatted = amount.toLocaleString('en-IN');
  return symbol ? `${symbol}${formatted}` : `${currency} ${formatted}`;
}
